import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getSellersRequest,
  downloadSellersRequest,
  getAdminSellersListRequest,
  getSellerWarehousesRequest,
  getCompanyWarehousesRequest,
  getOwnWarehousesRequest,
  createSellerRequest,
  checkSellerEmailRequest,
} from './sellersApi';
import { extractApiError, isApiUnreachable } from '../../utils/errors';
import { notificationStarted } from '../notifications/notificationsSlice';
import { NOTIFICATION_KIND } from '../notifications/notificationsConstants';

export const fetchSellers = createAsyncThunk(
  'sellers/fetchSellers',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { search, page, rowsPerPage } = getState().sellers.filters;

      const params = {
        page,
        page_size: rowsPerPage,
      };

      if (search) {
        params.search = search;
      }

      const response = await getSellersRequest(params);
      return response.data;
    } catch (error) {
      console.error('Error fetching sellers:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo obtener los sellers'));
    }
  }
);

/**
 * Loads every seller of the signed-in admin as select options, for the screens
 * that scope an action to one seller (order upload, among others).
 *
 * Skipped once loaded: the roster barely changes within a session.
 *
 * @returns {Promise<Array<{value: number, label: string}>>}
 */
export const fetchSellerOptions = createAsyncThunk(
  'sellers/fetchSellerOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAdminSellersListRequest();

      return (response.data?.serialized_sellers || []).map((seller) => ({
        value: seller.id,
        label: seller.company?.social_reason || `Seller ${seller.id}`,
        // Who owns it and which company it is. The admin's screens read only
        // `value` and `label`; staff needs both to narrow a roster that, for
        // it, is the whole platform — by operator, and then by company.
        adminId: seller.admin ?? null,
        companyId: seller.company?.id ?? null,
      }));
    } catch (error) {
      console.error('Error fetching seller options:', error);
      return rejectWithValue('No se pudo obtener la lista de sellers');
    }
  },
  {
    condition: (_, { getState }) => !getState().sellers.sellerOptionsLoaded,
  }
);

/**
 * Loads the warehouses of one seller as select options. Cleared whenever the
 * seller changes, so a stale warehouse can never be submitted for the wrong
 * seller.
 *
 * @param {number|string} sellerId
 * @returns {Promise<Array<{value: number, label: string}>>}
 */
export const fetchSellerWarehouseOptions = createAsyncThunk(
  'sellers/fetchSellerWarehouseOptions',
  async (sellerId, { rejectWithValue }) => {
    if (!sellerId) return rejectWithValue('Seller ID no provisto');

    try {
      const response = await getSellerWarehousesRequest(sellerId);
      const warehouses = Array.isArray(response.data) ? response.data : [];

      return warehouses.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
      }));
    } catch (error) {
      console.error('Error fetching seller warehouse options:', error);
      return rejectWithValue('No se pudieron obtener las bodegas del seller');
    }
  }
);

/**
 * The warehouses of a company, into the same `sellerWarehouseOptions`.
 *
 * Same job as `fetchSellerWarehouseOptions` through a different door:
 * `admins/seller_warehouses/<id>/` resolves the seller inside the signed-in
 * admin's own roster, so it throws for a seller of another operator — which is
 * every seller but a handful when the session is staff. This one asks by
 * company and has no such filter.
 *
 * @param {number|string} companyId
 * @returns {Promise<Array<{value: number, label: string}>>}
 */
export const fetchCompanyWarehouseOptions = createAsyncThunk(
  'sellers/fetchCompanyWarehouseOptions',
  async (companyId, { rejectWithValue }) => {
    if (!companyId) return rejectWithValue('Company ID no provisto');

    try {
      const response = await getCompanyWarehousesRequest(companyId);
      // This endpoint wraps its list — `{ serialized_warehouses: [...] }` —
      // where the seller one answers a bare array.
      const warehouses = response.data?.serialized_warehouses || [];

      return warehouses.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
      }));
    } catch (error) {
      console.error('Error fetching company warehouse options:', error);
      return rejectWithValue('No se pudieron obtener las bodegas de la empresa');
    }
  }
);

/**
 * Loads the signed-in seller's own warehouses as select options, into the same
 * `sellerWarehouseOptions` the admin's forms fill for the seller they picked. A
 * session is a single profile, so the two never compete for it.
 *
 * @returns {Promise<Array<{value: number, label: string}>>}
 */
export const fetchOwnWarehouseOptions = createAsyncThunk(
  'sellers/fetchOwnWarehouseOptions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getOwnWarehousesRequest();
      const warehouses = Array.isArray(response.data) ? response.data : [];

      return warehouses.map((warehouse) => ({
        value: warehouse.id,
        label: warehouse.name,
      }));
    } catch (error) {
      console.error('Error fetching own warehouse options:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener tus bodegas'));
    }
  }
);

/* -------------------------------------------------------------------------- */
/*                                  Creation                                  */
/* -------------------------------------------------------------------------- */

/**
 * Creates a seller from the wizard payload.
 *
 * Errors are read with `extractApiError` rather than replaced by a generic
 * message: this endpoint answers `{"error": "..."}` with the one thing that
 * went wrong — a taken email, or which warehouse is missing which field — and
 * it reports only the first failure, so the text is all the caller gets.
 *
 * @returns {Promise<{seller_id: number, company_id: number, user_email: string,
 *   user_temp_password: string, warehouses: number[], notification_email: boolean}>}
 */
export const createSeller = createAsyncThunk(
  'sellers/createSeller',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createSellerRequest(payload);
      return response.data;
    } catch (error) {
      console.error('Error creating seller:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo crear el seller'));
    }
  }
);

/**
 * Asks whether an email can still be used, before the create call commits to it.
 *
 * Worth the round trip because a duplicate email is not a clean rejection on
 * the backend: the check runs after the company and its address have already
 * been written, and the early exit does not roll them back.
 *
 * A failed lookup resolves with `available: null` instead of rejecting. Not
 * knowing must not block the form — the create call validates it anyway.
 *
 * @param {string} email
 * @returns {Promise<{email: string, available: boolean|null}>}
 */
export const checkSellerEmail = createAsyncThunk(
  'sellers/checkSellerEmail',
  async (email) => {
    try {
      const response = await checkSellerEmailRequest(email);
      return { email, available: Boolean(response.data?.email_verification) };
    } catch (error) {
      console.error('Error checking seller email:', error);
      return { email, available: null };
    }
  }
);

/**
 * Queues the sellers report.
 *
 * Like the deliveries export, this only starts a Celery job; the result lands
 * in the Centro de Actividad over the `download_sellers` socket channel.
 */
export const requestDownloadSellers = createAsyncThunk(
  'sellers/requestDownloadSellers',
  async (_, { dispatch, rejectWithValue, requestId }) => {
    dispatch(notificationStarted({ kind: NOTIFICATION_KIND.DOWNLOAD_SELLERS, requestId }));

    try {
      const response = await downloadSellersRequest();
      return response.data;
    } catch (error) {
      return rejectWithValue(extractApiError(error, 'No se pudo iniciar la descarga de sellers'), {
        unconfirmed: isApiUnreachable(error),
      });
    }
  }
);
